import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { hasPermission, getCalendarViewAccess } from '../../utils/permissions';

const EventDetailsModal = ({ 
  isOpen, 
  onClose, 
  viewingEvent, 
  studentEnrollment, 
  eventTypeColors,
  sendingReminders,
  handleSendReminders,
  handleCompleteLesson,
  handleEditEvent,
  handleDeleteClick,
  handleCancelEvent
}) => {
  const [showCancelOptions, setShowCancelOptions] = useState(false);
  if (!isOpen || !viewingEvent) return null;
  
  // Get current user to check if this is their event
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const viewAccess = getCalendarViewAccess();
  
  // Can only edit if user has update permission and either:
  // 1. Has 'all' view access, or
  // 2. Has 'own' view access and is the assigned tutor for this event
  const canEdit = hasPermission('calendar', 'update') && 
    (viewAccess === 'all' || (viewAccess === 'own' && viewingEvent.tutor_id === user.id));
  
  // Can only delete if user has delete permission and either:
  // 1. Has 'all' view access, or
  // 2. Has 'own' view access and is the assigned tutor for this event
  const canDelete = hasPermission('calendar', 'delete') && 
    (viewAccess === 'all' || (viewAccess === 'own' && viewingEvent.tutor_id === user.id));
    
  // Can only complete if user has complete permission and either:
  // 1. Has 'all' view access, or
  // 2. Has 'own' view access and is the assigned tutor for this event
  const canComplete = hasPermission('calendar', 'complete') && 
    (viewAccess === 'all' || (viewAccess === 'own' && viewingEvent.tutor_id === user.id));
    
  // Can only cancel if user has update permission and either:
  // 1. Has 'all' view access, or
  // 2. Has 'own' view access and is the assigned tutor for this event
  const canCancel = hasPermission('calendar', 'update') && 
    (viewAccess === 'all' || (viewAccess === 'own' && viewingEvent.tutor_id === user.id)) &&
    viewingEvent.status !== 'Cancelled' && viewingEvent.status !== 'Completed';
  
  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center"
      onClick={(e) => {
        // Only close if clicking directly on the background overlay
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-medium text-gray-900">Event Details</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-3">
          <div className="border-l-4 pl-3" style={{ borderColor: viewingEvent.backgroundColor || eventTypeColors.other }}>
            <h2 className="text-xl font-semibold">{viewingEvent.title}</h2>
            <div className="text-sm text-gray-500 capitalize">{viewingEvent.event_type || 'meeting'}</div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Start Time</h4>
              <p className="text-base text-gray-900">{moment(viewingEvent.start).format('MMM D, YYYY HH:mm')}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">End Time</h4>
              <p className="text-base text-gray-900">{moment(viewingEvent.end).format('MMM D, YYYY HH:mm')}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {viewingEvent.location && (
              <div>
                <h4 className="text-sm font-medium text-gray-500">Location</h4>
                <p className="text-base text-gray-900">{viewingEvent.location}</p>
              </div>
            )}
                {viewingEvent.country && (
                <div className="flex items-center space-x-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Country</h4>
                    <p className="text-base text-gray-900">{viewingEvent.country}</p>
                  </div>
                  
                  {/* Display event status inline with country */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Status</h4>
                    <p className={`text-base font-medium ${
                      viewingEvent.status === 'Completed' ? 'text-green-600' : 
                      viewingEvent.status === 'Cancelled' ? 'text-red-600' : 
                      'text-blue-600'
                    }`}>
                      {viewingEvent.status || 'Scheduled'}
                    </p>
                  </div>
                </div>
              )}
              
              {!viewingEvent.country && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Status</h4>
                  <p className={`text-base font-medium ${
                    viewingEvent.status === 'Completed' ? 'text-green-600' : 
                    viewingEvent.status === 'Cancelled' ? 'text-red-600' : 
                    'text-blue-600'
                  }`}>
                    {viewingEvent.status || 'Scheduled'}
                  </p>
                </div>
              )}
            </div>
            
            {/* Makeup event relationship information */}
            {viewingEvent.is_makeup && viewingEvent.original_event && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-1">Makeup Event</h4>
                <p className="text-sm text-blue-800">
                  This is a makeup event for a cancelled class on{' '}
                  {viewingEvent.original_event.start_time ? new Date(viewingEvent.original_event.start_time).toLocaleDateString() : 'the original date'}.
                </p>
                {viewingEvent.original_event.title && (
                  <p className="text-sm text-blue-800 mt-1">
                    Original event: {viewingEvent.original_event.title}
                  </p>
                )}
              </div>
            )}
            
            {/* Show makeup event info if this event has a makeup */}
            {viewingEvent.has_makeup && viewingEvent.makeup_event && (
              <div className="mt-3 p-3 bg-orange-50 rounded-md border border-orange-200">
                <h4 className="text-sm font-medium text-orange-800 mb-1">Has Makeup Event</h4>
                <p className="text-sm text-orange-800">
                  This cancelled event has a makeup event scheduled for{' '}
                  {viewingEvent.makeup_event.start_time ? new Date(viewingEvent.makeup_event.start_time).toLocaleDateString() : 'a later date'}.
                </p>
                {viewingEvent.makeup_event.title && (
                  <p className="text-sm text-orange-800 mt-1">
                    Makeup event: {viewingEvent.makeup_event.title}
                  </p>
                )}
              </div>
            )}

          {viewingEvent.event_type === 'course' && (
            <div className="space-y-4 mb-4">
              <div className="grid grid-cols-2 gap-4">
                {viewingEvent.course_id && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Course</h4>
                    <p className="text-base text-gray-900">
                      {viewingEvent.course_name || 'Unknown Course'}
                    </p>
                  </div>
                )}
                
                {viewingEvent.tutor_id && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Assigned Tutor</h4>
                    <p className="text-base text-gray-900 truncate" title={`${viewingEvent.tutor_name || 'Unknown Tutor'}${viewingEvent.tutor_university ? ` (${viewingEvent.tutor_university}${viewingEvent.tutor_study_program ? `, ${viewingEvent.tutor_study_program}` : ''})` : ''}`}>
                      {viewingEvent.tutor_name || 'Unknown Tutor'}
                      {viewingEvent.tutor_university && ` (${viewingEvent.tutor_university}`}
                      {viewingEvent.tutor_study_program && `, ${viewingEvent.tutor_study_program}`}
                      {viewingEvent.tutor_university && ')'}
                    </p>
                  </div>
                )}
              
              </div>
              
              {/* Student Enrollment Progress Bar */}
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Student Enrollment</span>
                  <span>{studentEnrollment.currentCount} / {studentEnrollment.maxStudents}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className={`h-2 rounded-full ${studentEnrollment.currentCount >= studentEnrollment.maxStudents ? 'bg-red-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(100, (studentEnrollment.currentCount / studentEnrollment.maxStudents) * 100)}%` }}
                  ></div>
                </div>
                
                {/* Student List */}
                {studentEnrollment.students && studentEnrollment.students.length > 0 && (
                  <div className="mt-2">
                    <h4 className="text-sm font-medium text-gray-600 mb-1">Students in this class:</h4>
                    <div className="bg-gray-50 rounded-md p-2 max-h-32 overflow-y-auto">
                      <ul className="divide-y divide-gray-200">
                        {studentEnrollment.students.map((student, index) => (
                          <li key={index} className="py-1 text-sm">
                            <span className="font-medium">{student.student_name || student.lead_name || 'Unknown'}</span>
                            {student.student_age && <span className="text-gray-500 ml-2">({student.student_age} years)</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Show related lead/deal for any event type */}
          {(viewingEvent.lead_name || viewingEvent.deal_title || viewingEvent.parent_email) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {viewingEvent.lead_name && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Related Lead</h4>
                  <p className="text-base text-gray-900">{viewingEvent.lead_name}</p>
                  {(viewingEvent.lead_email || viewingEvent.parent_email) && (
                    <p className="text-sm text-gray-600">{viewingEvent.lead_email || viewingEvent.parent_email}</p>
                  )}
                </div>
              )}
              
              {/* Show parent email when no lead name is available */}
              {!viewingEvent.lead_name && viewingEvent.parent_email && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Parent Email</h4>
                  <p className="text-sm text-gray-600">{viewingEvent.parent_email}</p>
                </div>
              )}
              
              {viewingEvent.deal_title && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Related Deal</h4>
                  <p className="text-base text-gray-900">{viewingEvent.deal_title}</p>
                </div>
              )}
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            {viewingEvent.description && (
              <div className="col-span-2">
                <h4 className="text-sm font-medium text-gray-500">Description</h4>
                <p className="text-base text-gray-900 whitespace-pre-wrap">{viewingEvent.description}</p>
              </div>
            )}
            
            {viewingEvent.created_by_name && (
              <div className="text-sm text-gray-500">
                Created by {viewingEvent.created_by_name}
              </div>
            )}
          </div>
          
          {/* Attendance Information (for completed events) */}
          {viewingEvent.status === 'Completed' && viewingEvent.attendance_data && (
            <div className="mt-4 border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Attendance Record</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-2 px-4 border-b text-left text-xs font-medium text-gray-500">Student</th>
                      <th className="py-2 px-4 border-b text-left text-xs font-medium text-gray-500">Status</th>
                      <th className="py-2 px-4 border-b text-left text-xs font-medium text-gray-500">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(viewingEvent.attendance_data).map(([dealId, data]) => {
                      // Find student info from the enrolled students
                      const studentInfo = studentEnrollment.students?.find(s => s.deal_id.toString() === dealId);
                      return (
                        <tr key={dealId}>
                          <td className="py-2 px-4 border-b text-sm">
                            {studentInfo?.student_name || studentInfo?.lead_name || 'Unknown'}
                          </td>
                          <td className="py-2 px-4 border-b text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs inline-block
                              ${data.status === 'present' ? 'bg-green-100 text-green-800' : ''}
                              ${data.status === 'absent' ? 'bg-red-100 text-red-800' : ''}
                              ${data.status === 'late' ? 'bg-yellow-100 text-yellow-800' : ''}
                            `}>
                              {data.status === 'present' ? 'Present' : 
                               data.status === 'absent' ? 'Absent' : 
                               data.status === 'late' ? 'Late' : data.status}
                            </span>
                          </td>
                          <td className="py-2 px-4 border-b text-sm">{data.notes || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap justify-end gap-2 pt-3">
            {/* Add Manage Students button only for course events */}
            {viewingEvent.event_type === 'course' && (
              <>
                {hasPermission('calendar', 'update') && (
                  <button
                    onClick={() => onClose('manage-students')}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md shadow-sm hover:bg-green-700"
                  >
                    Manage Students
                  </button>
                )}
                {/* Send reminders button for course events */}
                {hasPermission('calendar', 'update') && (
                  <button
                    onClick={() => handleSendReminders(viewingEvent.id)}
                    className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded-md shadow-sm hover:bg-yellow-700"
                    disabled={sendingReminders}
                  >
                    {sendingReminders ? 'Sending...' : 'Send Reminders'}
                  </button>
                )}
                {/* Send Email to All Students button for course events */}
                {hasPermission('calendar', 'email') && studentEnrollment.students && studentEnrollment.students.length > 0 && (
                  <button
                    onClick={() => onClose('email-students')}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700"
                  >
                    Email All Parents
                  </button>
                )}
                {/* Complete Lesson button - only show for course events that aren't already completed */}
                {viewingEvent.status !== 'Completed' && canComplete && (
                  <button
                    onClick={() => handleCompleteLesson(viewingEvent.id)}
                    className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md shadow-sm hover:bg-purple-700"
                  >
                    Complete Lesson
                  </button>
                )}
              </>
            )}
            
            {/* Cancel Event Button - only for events that can be cancelled */}
            {canCancel && !showCancelOptions && (
              <button
                onClick={() => setShowCancelOptions(true)}
                className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded-md shadow-sm hover:bg-orange-700"
              >
                Cancel Event
              </button>
            )}
            
            {/* Cancel Options Panel - Simplified */}
            {showCancelOptions && (
              <div
                className="fixed inset-0 bg-gray-900 bg-opacity-50 z-10 flex items-center justify-center overflow-hidden"
                onClick={(e) => {
                  // Only close if clicking directly on the background overlay
                  if (e.target === e.currentTarget) {
                    setShowCancelOptions(false);
                  }
                }}
              >
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 m-4" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-medium mb-4">Cancel Event</h3>
                  
                  <p className="mb-6">How would you like to handle this event?</p>
                  
                  <div className="flex flex-col space-y-4">
                    <button
                      onClick={() => {
                        handleCancelEvent('single', true);
                        setShowCancelOptions(false);
                      }}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 text-center font-medium"
                    >
                      Cancel and Schedule Makeup
                    </button>
                    
                    <button
                      onClick={() => {
                        handleCancelEvent('single', false);
                        setShowCancelOptions(false);
                      }}
                      className="w-full px-4 py-3 bg-gray-600 text-white rounded hover:bg-gray-700 text-center font-medium"
                    >
                      Cancel Only
                    </button>
                    
                    <button
                      onClick={() => setShowCancelOptions(false)}
                      className="w-full px-4 py-2 bg-white border-gray-300 border rounded hover:bg-gray-50 text-center"
                    >
                      Back
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {canDelete && (
              <button
                onClick={handleDeleteClick}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md shadow-sm hover:bg-red-700"
              >
                Delete
              </button>
            )}
            {canEdit && (
              <button
                onClick={handleEditEvent}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700"
              >
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md shadow-sm hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

EventDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  viewingEvent: PropTypes.object,
  studentEnrollment: PropTypes.object,
  eventTypeColors: PropTypes.object.isRequired,
  sendingReminders: PropTypes.bool,
  handleSendReminders: PropTypes.func.isRequired,
  handleCompleteLesson: PropTypes.func.isRequired,
  handleEditEvent: PropTypes.func.isRequired,
  handleDeleteClick: PropTypes.func.isRequired,
  handleCancelEvent: PropTypes.func.isRequired
};

export default EventDetailsModal;